import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ControlOutlined, FileAddOutlined, FileOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Modal, Row } from 'antd';

import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import AlpaClass from './Form/Alpaclass';
import File from './Form/File';
import Nutror from './Form/Nutror';
import Other from './Form/Other';
import DeliveryList from './List';
import Validations from './validations';
import { FormContent } from '../../EditProduct/components/FormContent';

interface IProps {
  deliveries: any;
  onChange?: any;
  fade?: any;
  loading?: boolean;
  onChangeRequiredFields?: any;
  isInfo?: boolean;
  offer?: any;
  ref?: any;
  newProduct?: boolean;
  isService?: boolean;
  enabledValidation?: boolean;
}

const ifHasNutror = (items: any[]) => (items || []).some(i => i.method_id == 24);
const ifHasHotmartFormat = (items: any[]) => (items || []).some(i => i.method_id == 4);

const Delivery: React.FC<IProps> = ({
  deliveries: deliveriesProp,
  onChange,
  loading,
  onChangeRequiredFields,
  isInfo,
  offer,
  newProduct,
  isService,
  enabledValidation
}) => {
  const [deliveries, setDeliveries] = useState<any>(deliveriesProp);
  const [modal, setModal] = useState(false);
  const [modalFile, setModalFile] = useState(false);
  const [modalNutror, setModalNutror] = useState(false);
  const [modalOther, setModalOther] = useState(false);
  const [modalAlpaClass, setModalAlpaClass] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any>([]);
  const prevPropsRef = useRef({ enabledValidation, deliveries: deliveriesProp });

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const changeState = useCallback(() => {
    const newValidationErrors = run({ deliveries: deliveriesProp }, Validations);
    setDeliveries(deliveriesProp);
    setValidationErrors(newValidationErrors);
    if (onChangeRequiredFields)
      onChangeRequiredFields(
        'deliveryRequiredFields',
        newValidationErrors ? Object.keys(newValidationErrors).length : 0
      );
  }, [deliveriesProp, onChangeRequiredFields]);

  useEffect(() => {
    if (!loading && enabledValidation) changeState(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const prevProps = prevPropsRef.current;
    if (
      (!prevProps.enabledValidation && enabledValidation) ||
      (enabledValidation && prevProps.deliveries !== deliveriesProp)
    )
      changeState();
    prevPropsRef.current = { enabledValidation, deliveries: deliveriesProp };
  }, [enabledValidation, deliveriesProp, changeState]);

  const toggleModal = useCallback(() => {
    setModal(prev => !prev);
  }, []);
  const toggleModalFile = useCallback(() => {
    setModalFile(prev => !prev);
  }, []);
  const toggleModalNutror = useCallback(() => {
    setModalNutror(prev => !prev);
  }, []);
  const toggleModalOther = useCallback(() => {
    setModalOther(prev => !prev);
  }, []);
  const toggleModalAlpaClass = useCallback(() => {
    setModalAlpaClass(prev => !prev);
  }, []);

  const selectDelivery = useCallback((delivery: 'FILES' | 'NUTROR' | 'OTHERS' | 'ALPACLASS' | 'PIN') => {
    switch (delivery) {
      case 'FILES':
        setModal(false);
        setModalFile(true);
        break;
      case 'NUTROR':
        setModal(false);
        setModalNutror(true);
        break;
      case 'OTHERS':
        setModal(false);
        setModalOther(true);
        break;
      case 'ALPACLASS':
        setModal(false);
        setModalAlpaClass(true);
        break;
      default:
    }
  }, []);

  const onChangeDeliveries = useCallback(
    (dels: any) => {
      if (!onChange) return;
      let files: any = {};
      let nutror: any = {};
      let others: any = [];
      let newDeliveries: any = [];
      const newFiles = dels?.length ? dels.filter((item: any) => item.method_id === '5') : false;
      let filesId: any = [];

      for (let i = 0; i < newFiles.length; i++) {
        const nf = newFiles[i];

        if (nf.files?.length) {
          filesId = filesId.concat(
            nf.files.map((fil: any) => (fil?.upload_id ? { upload_id: fil.upload_id, name: fil.name } : fil))
          );
        } else {
          filesId = filesId?.length ? filesId : false;
        }
      }

      if (Array.isArray(filesId) && filesId.length) {
        files.method_id = '5';
        files.files = filesId;
      } else {
        files = false;
      }

      nutror = ifHasNutror(dels) ? dels.filter((item: any) => item.method_id === '24')[0] : false;
      others = dels?.length ? dels.filter((item: any) => item.method_id !== '5' && item.method_id !== '24') : false;
      if (files) newDeliveries.push(files);
      if (nutror) newDeliveries.push(nutror);
      if (others) newDeliveries = newDeliveries.concat(others);
      onChange(newDeliveries);
    },
    [onChange]
  );

  const onEditDeliveries = useCallback(
    (dels: any) => {
      setDeliveries(dels);
      onChangeDeliveries(dels);
    },
    [onChangeDeliveries]
  );
  const onAddNewItem = useCallback(
    (item: any) => {
      const cd = deliveries && Array.isArray(deliveries) ? deliveries : [];
      const nd = [...cd, item];
      setDeliveries(nd);
      setModalFile(false);
      setModalOther(false);
      setModalNutror(false);
      onChangeDeliveries(nd);
    },
    [deliveries, onChangeDeliveries]
  );

  const showOtherDeliveries = useCallback(() => {
    const isProductFree = offer.charge_type === 'G';
    const isBeta = eduzzAccountsService.verifyUserHasBetaModule('allow_all_deliveries_for_free_charge_product');
    const showAllDeliveries = true;
    return !isProductFree || (newProduct && isBeta) || showAllDeliveries;
  }, [offer, newProduct]);

  const title = isService ? 'Entregas de arquivo (opcional)' : 'Entregas de produto';

  return (
    <FormContent
      title={title}
      loading={loading}
      action={
        <Button
          id='product-add-delivery'
          size='small'
          onClick={toggleModal}
          type='primary'
          block
          style={{ float: 'right' }}
          icon={<FileAddOutlined />}
        >
          Adicionar Entrega
        </Button>
      }
    >
      <Row gutter={[16, 16]} align='middle'>
        {isInfo && !isService && isValid('deliveries') && errorFor('deliveries') && (
          <Col span={24}>
            <span className='text-xs text-red-500'>Obrigatório pelo menos uma entrega cadastrada.</span>
          </Col>
        )}
        {isService && (
          <Col span={24}>
            <span className='text-xs text-gray-500'>
              Para vendas do tipo Serviço, você pode adicionar uma entrega se desejar, mas não é obrigatório.
            </span>
          </Col>
        )}
        <Col span={24}>
          <DeliveryList deliveries={deliveries} onEditSomething={onEditDeliveries} offer={offer} />
        </Col>

        <Modal
          open={modal}
          onCancel={toggleModal}
          footer={
            <Button onClick={toggleModal} type='text'>
              Fechar
            </Button>
          }
          title={
            <>
              Tipo de entrega
              {!showOtherDeliveries() && (
                <Alert
                  type='warning'
                  message='Para produtos gratuitos só é possível utilizar as entregas fornecidas pela Eduzz'
                  className='mt-2'
                />
              )}
            </>
          }
        >
          <ul className='mt-4! mb-0! list-none p-0'>
            <li
              className='flex cursor-pointer items-center gap-3 border-b border-gray-200 px-4 py-3 hover:bg-gray-50'
              onClick={() => selectDelivery('FILES')}
              id='product-delivery-files'
            >
              <FileOutlined style={{ fontSize: 40 }} />
              <div>
                <div className='font-medium'>Arquivos</div>
                <div className='text-sm text-gray-500'>{"PDF's, documentos, imagens e e-books."}</div>
              </div>
            </li>
            {!isService && (
              <>
                {!ifHasNutror(deliveries) && (
                  <li
                    className='flex cursor-pointer items-center gap-3 border-b border-gray-200 px-4 py-3 hover:bg-gray-50'
                    onClick={() => selectDelivery('NUTROR')}
                    id='product-delivery-nutror'
                  >
                    <img src='/img/nutror-symbol.svg' width='40' />
                    <div>
                      <div className='font-medium'>Nutror</div>
                      <div className='text-sm text-gray-500'>Nossa plataforma de membros.</div>
                    </div>
                  </li>
                )}
                <li
                  className='flex cursor-pointer items-center gap-3 border-b border-gray-200 px-4 py-3 hover:bg-gray-50'
                  onClick={() => selectDelivery('ALPACLASS')}
                  id='product-delivery-alpaclass'
                >
                  <img src='/img/alpaclass.svg' width='40' />
                  <div>
                    <div className='font-medium'>
                      <span style={{ marginRight: 10 }}>Alpaclass</span>
                      <span style={{ color: '#8ade8a', fontWeight: 500 }}>Novo</span>
                    </div>
                    <div className='text-sm text-gray-500'>Plataforma de área de membros.</div>
                  </div>
                </li>
                {showOtherDeliveries() && (
                  <li
                    className='flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50'
                    onClick={() => selectDelivery('OTHERS')}
                    id='product-delivery-others'
                  >
                    <ControlOutlined style={{ fontSize: 40 }} />
                    <div>
                      <div className='font-medium'>Outros</div>
                      <div className='text-sm text-gray-500'>Weve, customizado e outros.</div>
                    </div>
                  </li>
                )}
              </>
            )}
          </ul>
        </Modal>

        <File isModal={true} open={modalFile} onClose={toggleModalFile} onOk={onAddNewItem} />
        <Nutror isModal={true} open={modalNutror} onClose={toggleModalNutror} onOk={onAddNewItem} />
        <Other
          isModal={true}
          deliveries={deliveriesProp}
          open={modalOther}
          newProduct={newProduct}
          onClose={toggleModalOther}
          onOk={onAddNewItem}
          hasHotmart={ifHasHotmartFormat(deliveries)}
        />
        <AlpaClass
          isModal={true}
          deliveries={deliveriesProp}
          open={modalAlpaClass}
          newProduct={newProduct}
          onClose={toggleModalAlpaClass}
          onOk={onAddNewItem}
          offer={offer}
        />
      </Row>
    </FormContent>
  );
};

export default Delivery;
